import { ChangeEventHandler, useEffect, useState } from 'react';
import { RotatingLines } from 'react-loader-spinner';
import Modal from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import detailsInputStyles from '../styles/detailsInput.module.css';
import EbcSolarApi from '../lib/api';
import useEbcSolarStore from '../store/ebc-solar-store';

type CTASqueezePopupModalProps = {
    open: boolean;
    onClose: () => void;
};
const CTASqueezePopupModal = ({ open, onClose }: CTASqueezePopupModalProps) => {
    const regionId = document.getElementById('solar-quotation-widget')?.getAttribute('data-region-id') || 'EBC681924';

    const {address, priceEstimate, solarPanelType, solarPanelAmount, batterySelected} = useEbcSolarStore((state) => ({
        address: state.address,
        priceEstimate: state.quotationData.totalPrice,
        solarPanelType: state.quotationData.solarPanelType,
        solarPanelAmount: state.quotationData.numberOfSolarPanels,
        batterySelected: state.quotationData.batteryEnabled,
    }))

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');

    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const validateName = () => {
        if (!name || name.length < 2) {
            setNameError('Name is required');
            return false;
        }

        setNameError('');
        return true;
    };

    const validatePhone = () => {
        if (!phone) {
            setPhoneError('Phone is required');
            return false;
        }

        // Regular expression for UK phone number
        const ukPhoneRegex = /^(?:\d\s?){9,11}$/;

        if (!ukPhoneRegex.test(phone)) {
            setPhoneError('Invalid UK phone number format');
            return false;
        }

        setPhoneError('');
        return true;
    };

    const validateEmail = () => {
        if (!email) {
            setEmailError('Email is required');
            return false;
        }

        // Regular expression for validating an email address
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

        if (!emailRegex.test(email)) {
            setEmailError('Invalid email address');
            return false;
        }

        setEmailError('');
        return true;
    };

    const handleNameChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setName(e.target.value);
    };

    const handlePhoneChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setPhone(e.target.value);
    };

    const handleEmailChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setEmail(e.target.value);
    };

    const handleSubmit = async () => {
        if (loading || success) {
            return;
        }
        if (!validateName() || !validatePhone() || !validateEmail()) {
            return;
        }

        setLoading(true);
        
        const ok = await EbcSolarApi.submitSolarQuoteForm(
            name,
            phone,
            email,
            regionId,
            address,
            priceEstimate,
            solarPanelType,
            solarPanelAmount,
            batterySelected
        );

        setLoading(false);

        if(ok === true) {
            // Add some delay so it feels like it's actually doing something
            // Request finishes too quickly.
            setTimeout(() => {
                setSuccess(true);
            }, 1500)
        } else {
            setError('Something went wrong. Please try again later.');
        }
    };

    useEffect(() => {
        if (open === false) {
            setName('');
            setPhone('');
            setEmail('');
            setNameError('');
            setPhoneError('');
            setEmailError('');
            setError('');
            setLoading(false);
            setSuccess(false);
        }
    }, [open]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            center
            styles={{ root: { zIndex: 999999 }, modal: { width: '720px', maxWidth: '80%', borderRadius: '8px' } }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    padding: '1rem',
                    borderRadius: '8px',
                    width: '100%',
                }}
            >
                {success === true ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem'
                    }}>
                        <img width={64} height='auto' alt='Tick' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenCircleTick.svg' />
                        <span style={{ textAlign: 'center', fontSize: '28px', fontWeight: 600, color: '#212121' }}>Submitted!</span>
                        <span style={{ textAlign: 'center', fontSize: '16px', color: '#212121', lineHeight: 1.5 }}>
                            Your details have been submitted successfully. One of our team members will be in touch with you via email or phone as soon as possible.
                        </span>

                    </div>
                ) : (
                    <>
                        <span style={{ fontSize: '28px', fontWeight: 600, color: '#212121' }}>Confirm Your Price</span>
                        <span style={{ fontSize: '14px', color: '#212121', lineHeight: 1.5 }}>
                            One of our team members will be in touch with you as soon as possible once you've submitted your details.
                        </span>

                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.25rem',
                            }}
                        >
                            <label htmlFor='name'>Full Name</label>
                            <input
                                value={name}
                                onChange={handleNameChange}
                                className={detailsInputStyles.detailsInput}
                                style={{ width: '100%', fontSize: '18px', fontFamily: 'Montserrat', padding: '0.5rem', borderRadius: '4px', border: '1px solid #222222' }}
                                type='text'
                                id='name'
                            />
                            <span style={{ fontSize: '14px', color: '#e92828' }}>{nameError}</span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.25rem',
                            }}
                        >
                            <label htmlFor='phone'>Phone Number</label>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                <span style={{ fontSize: '18px', fontWeight: 500 }}>+44</span>
                                <input
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    className={detailsInputStyles.detailsInput}
                                    style={{ width: '100%', fontSize: '18px', fontFamily: 'Montserrat', padding: '0.5rem', borderRadius: '4px', border: '1px solid #222222' }}
                                    type='text'
                                    id='phone'
                                />
                            </div>
                            <span style={{ fontSize: '14px', color: '#e92828' }}>{phoneError}</span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.25rem',
                            }}
                        >
                            <label htmlFor='email'>Email Address</label>
                            <input
                                value={email}
                                onChange={handleEmailChange}
                                className={detailsInputStyles.detailsInput}
                                style={{ width: '100%', fontSize: '18px', fontFamily: 'Montserrat', padding: '0.5rem', borderRadius: '4px', border: '1px solid #222222' }}
                                type='email'
                                id='email'
                            />
                            <span style={{ fontSize: '14px', color: '#e92828' }}>{emailError}</span>
                        </div>

                        <span style={{ fontSize: '14px', color: '#212121', lineHeight: 1.5 }}>
                            By clicking submit, you give us consent to contact you via the provided phone number and email address for communication and
                            promotional purposes
                        </span>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                paddingLeft: '4rem',
                                paddingRight: '4rem',
                                paddingTop: '1rem',
                                paddingBottom: '1rem',
                                borderRadius: '32px',
                                color: '#FFFFFF',
                                fontSize: '16px',
                                fontWeight: 700,
                                background: '#26A757',
                                cursor: 'pointer',
                                textWrap: 'nowrap',
                            }}
                            onClick={handleSubmit}
                        >
                            {loading ? (
                                <RotatingLines strokeColor='#FFFFFF' strokeWidth='5' animationDuration='0.75' width='24' visible={true} />
                            ) : (
                                <>Submit</>
                            )}
                        </div>
                        <span style={{ fontSize: '14px', color: '#e92828' }}>{error}</span>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default CTASqueezePopupModal;
